<template>
    <framework>
        <template #side>
            <side-panel-nav/>
        </template>
        <template #main>
            <main-header title="Conference Records" icon="fa-screen-users"/>
            <p class="mt-6 has-text-white" v-if="!registrations"><i class="fas fa-spin fa-spinner fa-fw mr-3"></i>Please wait. Loading your records...</p>

            <!-- Empty state sits outside the list column so it centers on the whole panel -->
            <empty-hero v-else-if="registrations.length === 0" icon="fa-screen-users" title="No conference records yet" text="Conferences and trainings you have registered for will show up here.">
                <b-button tag="router-link" :to="{name: 'conference-center'}" type="is-light"><i class="fad fa-presentation mr-2"></i>Browse the Conference Center</b-button>
            </empty-hero>

            <div v-else class="mt-6 content">
                <div class="columns">
                    <div class="column is-list">
                        <div>
                            <section class="mb-6 pb-2" v-if="recentRegistrations.length">
                                <section-header title="Recently Registered" icon="fa-arrow-right" :count="recentRegistrations.length"/>
                                <registration :registration="r" v-for="r in recentRegistrations" class="mb-5"/>

                            </section>
                            <section class="mb-6 pb-2" v-if="featuredConferences.length">
                                <section-header title="Active & Recent Conferences" icon="fa-calendar-alt" :count="featuredConferences.length"/>
                                <registration :registration="r" v-for="r in featuredConferences" class="mb-5"/>

                            </section>
                            <section class="mb-6 pb-2" v-if="alertRegistrations.length">
                                <section-header title="Records Requiring Attention" icon="fa-exclamation-triangle" :count="alertRegistrations.length"/>
                                <registration :registration="r" v-for="r in alertRegistrations" class="mb-5"/>

                            </section>
                            <section class="mb-6 pb-2">
                                <section-header title="All Conference Records" icon="fa-screen-users" :count="registrations.length"/>

                                <!-- One year at a time; the current (or most recent) year is selected first -->
                                <year-tabs :years="recordYearTabs" :value="activeRecordYear" @input="recordYear = $event"/>
                                <empty-state v-if="getByYear(activeRecordYear).length === 0" icon="fa-info-circle" :text="'No conference records in ' + activeRecordYear + '.'"/>
                                <registration :registration="r" v-for="r in getByYear(activeRecordYear)" :key="r.id" class="mb-5"/>
                            </section>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </framework>
</template>

<script>
    import MainHeader from "../../components/framework/MainHeader";
    import Framework from "../../components/framework/Framework";
    import SidePanelNav from "../../components/framework/SidePanelNav";
    import {client as http} from "../../http_client";
    import Registration from "../../components/Registration";
    import YearTabs from "../../components/framework/YearTabs";
    import EmptyState from "../../components/framework/EmptyState";
    import {defaultYearKey} from "../../utils/yearBuckets";
    import SectionHeader from "../../components/framework/SectionHeader";
    import EmptyHero from "../../components/framework/EmptyHero";
    import {mapActions, mapGetters} from "vuex";
    import moment from "moment/moment";
    import Conference from "../../components/Conference.vue";

    export default {
        components: {YearTabs, SectionHeader, EmptyHero, EmptyState, Conference, Registration, Framework, SidePanelNav, MainHeader},
        data: function() {
            return {
                showCompleted: false,
                recordYear: null
            };
        },
        computed: {
            ...mapGetters(['registrations']),
            recordYearTabs: function() {
                return this.recordYears.map(y => ({key: y, label: y, count: this.getByYear(y).length}));
            },
            activeRecordYear: function() {
                return this.recordYears.includes(this.recordYear) ? this.recordYear : this.defaultOpenYear;
            },
            defaultOpenYear: function() {
                return defaultYearKey(this.recordYears);
            },
            recentRegistrations: function() {
                if (this.registrations) {
                    return this.registrations.filter(r => {
                        let created = moment(r.created_at);
                        return (created.isBetween(moment(), moment().clone().add(30, 'days')) ||
                            created.isBetween(moment().clone().subtract(14, 'days'), moment()));
                    });
                } else {
                    return [];
                }
            },
            alertRegistrations: function() {
                if (this.registrations) {
                    return this.registrations.filter(r => {
                        return r.isIncompleteApps;
                    });
                } else {
                    return [];
                }
            },
            featuredConferences: function() {
                if (this.registrations) {
                    return this.registrations.filter(r => {
                        let start = moment(r.conferences.start_at);
                        let end = moment(r.conferences.end_at);
                        return ((start.isBetween(moment(), moment().clone().add(30, 'days')) ||
                                    start.isBetween(moment().clone().subtract(14, 'days'), moment())) ||
                                (end.isBetween(moment(), moment().clone().add(30, 'days')) ||
                                    end.isBetween(moment().clone().subtract(14, 'days'), moment())))
                            && !this.recentRegistrations.includes(r);
                    });
                } else {
                    return [];
                }
            },
            recordYears: function() {
                if (this.registrations) {
                    // The current year is always a tab, like the other archives
                    const years = new Set([new Date().getFullYear()]);
                    this.registrations.forEach(r => {
                        const startYear = new Date(r.conferences.start_at).getFullYear();
                        const endYear = new Date(r.conferences.end_at).getFullYear();
                        for (let y = startYear; y <= endYear; y++) {
                            years.add(y);
                        }
                    });
                    return [...years].sort((a, b) => b - a);
                } else {
                    return [];
                }
            }
        },
        methods: {
            ...mapActions(['getRegistrations']),
            getByYear(year) {
                if (this.registrations) {
                    return this.registrations.filter(r => {
                        const startYear = new Date(r.conferences.start_at).getFullYear();
                        const endYear = new Date(r.conferences.end_at).getFullYear();
                        return year >= startYear && year <= endYear;
                    });
                } else {
                    return [];
                }
            }
        },
        mounted() {
            this.getRegistrations();
        }
    };
</script>

<style lang="scss" scoped>
    h2 {
        font-size: 1.2rem;
        color: white;
        padding-bottom: 0.5rem;
        margin-bottom: 1rem;
        border-bottom: 1px dashed rgba(255, 255, 255, 0.2);

        i {
            color: white;
        }
    }
</style>


